import React, { useRef } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { HiOutlinePhotograph, HiX } from "react-icons/hi";
import { Spinner } from "./Spinner";
import NoTranslate from "./NoTranslate";
import useAuth from "../../hooks/useAuth";
import { useUploadFile } from "../../hooks/cdn";

interface WhatsAppImageUploadProps {
  value?: string | null;
  onUploaded: (url: string) => void;
  onRemoved: () => void;
}

// WhatsApp pins the image origin on an image template, so every image must share one fixed CDN origin.
const WhatsAppImageUpload: React.FC<WhatsAppImageUploadProps> = ({
  value,
  onUploaded,
  onRemoved,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadFile, isLoading } = useUploadFile();

  const k = (s: string) =>
    `dashboard.entities.viewEntityUser.sendWhatsApp.imageUpload.${s}`;

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error(t(k("invalidType")) as string);
      return;
    }
    if (file.size > 5 * 1048576) {
      toast.error(t(k("tooLarge")) as string);
      return;
    }
    uploadFile(
      {
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        data: { file } as any,
      },
      {
        onSuccess: (res: any) => {
          const cdnPath = res?.data?.element?.[0]?.cdnPath;
          if (cdnPath) {
            onUploaded(cdnPath);
          } else {
            toast.error(t(k("uploadError")) as string);
          }
        },
      }
    );
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
      />
      {value ? (
        <div className="relative w-full max-w-xs">
          <NoTranslate>
            <img
              src={value}
              alt=""
              className="h-32 w-full rounded-lg border border-gray-300 object-cover"
            />
          </NoTranslate>
          <button
            type="button"
            onClick={onRemoved}
            className="absolute right-2 top-2 rounded-full bg-red-500 p-1.5 text-white hover:bg-red-600"
            aria-label={t(k("remove")) as string}
          >
            <HiX size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={isLoading}
          className="flex h-32 w-full max-w-xs flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 transition-colors hover:border-blue-500 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <Spinner size="md" />
              <span className="text-xs text-gray-600">{t(k("uploading"))}</span>
            </>
          ) : (
            <>
              <HiOutlinePhotograph className="text-gray-400" size={28} />
              <span className="text-xs text-gray-600">{t(k("click"))}</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default WhatsAppImageUpload;
